import {
  GATE_DIAMETER,
  GRID_LINE_HEIGHT,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import { isChecklistHelperVisible } from '../../services/checklistMutationService'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import { createStripboardRecordDefaults } from '../../utils/stripboardDefaults'

export type IngestedNodeDraft = {
  title?: string
  description?: string
  weightage?: number
  estimatedHours?: number | null
  urgencyStars?: number | null
  importanceStars?: number | null
  prerequisites?: string[]
}

export type ChecklistCreateRequest = {
  parentContainerId?: string
  position?: { x: number; y: number }
}

export const isGateNode = (node: { nodeKind?: string }): boolean =>
  node.nodeKind === 'GATE_AND' || node.nodeKind === 'GATE_OR'
export const CHECKLIST_BOTTOM_CLEARANCE = 100
export const EMPTY_BOARD_MIN_WIDTH = 900
export const EMPTY_BOARD_HEIGHT = 750
export const MIN_VIEWPORT_WIDTH = 720
export const MIN_VIEWPORT_HEIGHT = 320
export const DEFAULT_VIEWPORT_HEIGHT = 750
export const legacyDashboardBoardRootId = (workspaceId: string) => `board-root:${workspaceId}:DASHBOARD`
export const getNodeDimensions = (node: { nodeKind?: string }) => isGateNode(node)
  ? { width: GATE_DIAMETER, height: GATE_DIAMETER }
  : { width: NODE_WIDTH, height: NODE_HEIGHT }

export type BoardItemInput = Partial<{
  [K in keyof BoardChecklistItem]: BoardChecklistItem[K] | null
}> & { text?: string; sourceChecklistId?: string | null }

export const normalizeChecklistPrerequisiteIds = (data: BoardItemInput) => {
  // `prerequisites` is the authoritative wire graph — trust it verbatim when
  // present. Do NOT merge `blockedByNodeIds`: that field is denormalized on
  // save (gate inputs are flattened up through the gate as leaf blockers for
  // `checklistDependencyState`) and re-adding it here duplicates every gate
  // input as a phantom direct wire from source → target after reload.
  if (Array.isArray(data.prerequisites) && data.prerequisites.length > 0) {
    return data.prerequisites.filter((candidate): candidate is string => typeof candidate === 'string')
  }
  // Legacy compat — some old rows only carry `blockedByNodeIds`.
  const ids: string[] = []
  if (Array.isArray(data.blockedByNodeIds)) {
    data.blockedByNodeIds.forEach((link) => {
      if (link?.targetType === 'CHECKLIST' && typeof link.targetId === 'string') ids.push(link.targetId)
    })
  }
  return ids
}

export const snapYToGrid = (y: number) => Math.max(0, Math.round(y / GRID_LINE_HEIGHT) * GRID_LINE_HEIGHT)
export const normalizeWeightage = (weightage: unknown) =>
  typeof weightage === 'number' && Number.isFinite(weightage) && weightage > 1
    ? Math.round(weightage)
    : 1
export const normalizeNonNegativeMetric = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
export const normalizeComplexityStars = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(5, Math.max(1, Math.round(value)))
    : 1

export const normalizeBoardItem = (id: string, data: BoardItemInput, index: number): BoardChecklistItem => ({
  ...createStripboardRecordDefaults(),
  id,
  parentContainerId: typeof data.parentContainerId === 'string' ? data.parentContainerId : '',
  workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
  board: data.board === 'VISIONBOARD' || data.board === 'ARCHIVE' || data.board === 'BIN' ? data.board : 'DASHBOARD',
  title: typeof data.title === 'string'
    ? data.title
    : typeof data.text === 'string' && data.text.trim()
      ? data.text
      : 'Checklist node',
  boardStatus: data.boardStatus === null || data.boardStatus === 'TODO' || data.boardStatus === 'IN_PROGRESS' || data.boardStatus === 'COMPLETE'
    ? data.boardStatus
    : data.workflowStatus === 'COMPLETED' || data.workflowStatus === 'NOT_REQUIRED'
      ? 'COMPLETE'
      : data.workflowStatus === 'ONGOING' || data.workflowStatus === 'POSTPONED'
        ? 'IN_PROGRESS'
        : data.workflowStatus || data.startDate || data.scheduleStartDate
          ? 'TODO'
          : null,
  priority: typeof data.priority === 'number' ? Math.max(1, Math.min(5, Math.round(data.priority))) : Math.ceil(((data.urgencyStars ?? 1) + (data.importanceStars ?? 1)) / 2),
  startDate: data.startDate ?? data.scheduleStartDate ?? null,
  endDate: data.endDate ?? data.scheduleEndDate ?? null,
  // Parent-scoped recurring range. Written by checklistWriter / TimelineContext
  // from the parent element's dates. Without this line the field is dropped
  // during normalization and every DAILY/WEEKLY node lands with an undefined
  // recurringWindow — which makes the canvas visibility filter treat the item
  // as "always visible on every focused day", so AI-generated recurring items
  // whose parent is scheduled to start next month still render on today's
  // Daily / Weekly canvas.
  recurringWindow: data.recurringWindow
    && typeof data.recurringWindow.parentElementId === 'string'
    && typeof data.recurringWindow.startDate === 'string'
    && typeof data.recurringWindow.endDate === 'string'
    && /^\d{4}-\d{2}-\d{2}/.test(data.recurringWindow.startDate)
    && /^\d{4}-\d{2}-\d{2}/.test(data.recurringWindow.endDate)
      ? {
          parentElementId: data.recurringWindow.parentElementId,
          startDate: data.recurringWindow.startDate.slice(0, 10),
          endDate: data.recurringWindow.endDate.slice(0, 10),
        }
      : undefined,
  dependencyNodeIds: Array.isArray(data.dependencyNodeIds) ? data.dependencyNodeIds : normalizeChecklistPrerequisiteIds(data),
  invalidDependencyNodeIds: Array.isArray(data.invalidDependencyNodeIds) ? data.invalidDependencyNodeIds : [],
  recordVersion: typeof data.recordVersion === 'number' && data.recordVersion > 0 ? data.recordVersion : 1,
  statusSyncOrigin: data.statusSyncOrigin ?? null,
  statusSyncVersion: data.statusSyncVersion ?? 0,
  description: typeof data.description === 'string' ? data.description : '',
  completionCriteria: typeof data.completionCriteria === 'string' ? data.completionCriteria : '',
  createdById: typeof data.createdById === 'string' ? data.createdById : '',
  createdByName: typeof data.createdByName === 'string' ? data.createdByName : '',
  createdAt: data.createdAt,
  assigneeId: typeof data.assigneeId === 'string' ? data.assigneeId : null,
  assigneeName: typeof data.assigneeName === 'string' ? data.assigneeName : null,
  assigneeHistoryIds: Array.isArray(data.assigneeHistoryIds) ? data.assigneeHistoryIds.filter((value): value is string => typeof value === 'string') : [],
  updatedAt: data.updatedAt,
  lastUpdatedBy: typeof data.lastUpdatedBy === 'string' ? data.lastUpdatedBy : null,
  lastUpdatedByName: typeof data.lastUpdatedByName === 'string' ? data.lastUpdatedByName : null,
  isCompleted: Boolean(data.isCompleted),
  isHelperVisible: isChecklistHelperVisible(data.isHelperVisible),
  weightage: typeof data.weightage === 'number' && data.weightage > 0 ? data.weightage : 1,
  position: data.position && Number.isFinite(data.position.x) && Number.isFinite(data.position.y)
    ? { x: data.position.x, y: data.position.y }
    : { x: 100, y: index * 120 + 50 },
  workspaceCanvasLayoutVersion: typeof data.workspaceCanvasLayoutVersion === 'number'
    ? data.workspaceCanvasLayoutVersion
    : 0,
  prerequisites: normalizeChecklistPrerequisiteIds(data),
  blockedByExternal: data.blockedByExternal ?? null,
  blockedByExternalId: typeof data.blockedByExternalId === 'string'
    ? data.blockedByExternalId
    : data.blockedByExternal?.targetId ?? null,
  interWorkspaceTargetId: typeof data.interWorkspaceTargetId === 'string' ? data.interWorkspaceTargetId : null,
  dependencyLogicGate: data.dependencyLogicGate === 'ANY_PREREQUISITE_MET'
    ? 'ANY_PREREQUISITE_MET'
    : 'ALL_PREREQUISITES_MET',
  hasAutoSpawnTrigger: data.hasAutoSpawnTrigger === true,
  autoSpawnTemplates: Array.isArray(data.autoSpawnTemplates)
    ? data.autoSpawnTemplates
        .filter((template) => template && typeof template.title === 'string')
        .map((template) => ({
          title: template.title,
          weightage: normalizeWeightage(template.weightage),
          relativeYOffset: typeof template.relativeYOffset === 'number' && Number.isFinite(template.relativeYOffset)
            ? template.relativeYOffset
            : GRID_LINE_HEIGHT,
        }))
    : [],
  requiresProofFile: data.requiresProofFile === true,
  proofFileUrl: typeof data.proofFileUrl === 'string' ? data.proofFileUrl : null,
  proofFileName: typeof data.proofFileName === 'string' ? data.proofFileName : null,
  proofFileStoragePath: typeof data.proofFileStoragePath === 'string' ? data.proofFileStoragePath : null,
  cardBackgroundFileId: typeof data.cardBackgroundFileId === 'string' ? data.cardBackgroundFileId : null,
  cardBackgroundUrl: typeof data.cardBackgroundUrl === 'string' ? data.cardBackgroundUrl : null,
  cardBackgroundFileName: typeof data.cardBackgroundFileName === 'string' ? data.cardBackgroundFileName : null,
  cardBackgroundStoragePath: typeof data.cardBackgroundStoragePath === 'string' ? data.cardBackgroundStoragePath : null,
  proofUploadedById: typeof data.proofUploadedById === 'string' ? data.proofUploadedById : null,
  proofUploadedByName: typeof data.proofUploadedByName === 'string' ? data.proofUploadedByName : null,
  proofUploadedAt: typeof data.proofUploadedAt === 'string' ? data.proofUploadedAt : null,
  requiresCoSign: data.requiresCoSign === true,
  coSignerRoleTarget: typeof data.coSignerRoleTarget === 'string' ? data.coSignerRoleTarget : 'ADMIN',
  coSignedByUid: typeof data.coSignedByUid === 'string' ? data.coSignedByUid : null,
  coSignedByName: typeof data.coSignedByName === 'string' ? data.coSignedByName : null,
  isPendingReview: data.isPendingReview === true,
  requiresLocationPin: data.requiresLocationPin === true,
  targetCoordinates: data.targetCoordinates && Number.isFinite(data.targetCoordinates.lat) && Number.isFinite(data.targetCoordinates.lng)
    ? data.targetCoordinates
    : null,
  verifiedCoordinates: data.verifiedCoordinates && Number.isFinite(data.verifiedCoordinates.lat) && Number.isFinite(data.verifiedCoordinates.lng)
    ? data.verifiedCoordinates
    : null,
  proofVideoUrl: typeof data.proofVideoUrl === 'string' ? data.proofVideoUrl : null,
  memoStoragePath: typeof data.memoStoragePath === 'string' ? data.memoStoragePath : null,
  memoFileName: typeof data.memoFileName === 'string' ? data.memoFileName : null,
  memoContentType: typeof data.memoContentType === 'string' ? data.memoContentType : null,
  memoUploadedById: typeof data.memoUploadedById === 'string' ? data.memoUploadedById : null,
  memoUploadedByName: typeof data.memoUploadedByName === 'string' ? data.memoUploadedByName : null,
  memoUploadedAt: typeof data.memoUploadedAt === 'string' ? data.memoUploadedAt : null,
  countdownHours: typeof data.countdownHours === 'number' && Number.isFinite(data.countdownHours) ? data.countdownHours : null,
  timerStartedAt: typeof data.timerStartedAt === 'string' ? data.timerStartedAt : null,
  timerExpiredAt: typeof data.timerExpiredAt === 'string' ? data.timerExpiredAt : null,
  isUrgentCrisisMode: data.isUrgentCrisisMode === true,
  enforcePaintSpringDelay: data.enforcePaintSpringDelay === true,
  delayDurationHours: typeof data.delayDurationHours === 'number' && Number.isFinite(data.delayDurationHours)
    ? data.delayDurationHours
    : 0,
  commentsCount: typeof data.commentsCount === 'number' && Number.isFinite(data.commentsCount) ? data.commentsCount : 0,
  voiceMemoBlobUrl: typeof data.voiceMemoBlobUrl === 'string' ? data.voiceMemoBlobUrl : null,
  isDescriptionPrivate: data.isDescriptionPrivate === true,
  estimatedHours: normalizeNonNegativeMetric(data.estimatedHours),
  actualCost: normalizeNonNegativeMetric(data.actualCost),
  budgetLimit: typeof data.budgetLimit === 'number' ? data.budgetLimit : data.budgetLimit === null ? null : undefined,
  estimatedCost: typeof data.estimatedCost === 'number' ? data.estimatedCost : data.estimatedCost === null ? null : undefined,
  actualSpend: typeof data.actualSpend === 'number' ? data.actualSpend : data.actualSpend === null ? null : undefined,
  complexityStars: normalizeComplexityStars(data.complexityStars),
  urgencyStars: normalizeComplexityStars(data.urgencyStars),
  importanceStars: normalizeComplexityStars(data.importanceStars),
  currencyCode: typeof data.currencyCode === 'string' ? data.currencyCode : 'USD',
  scheduleMode: data.scheduleMode === 'DURATION' ? 'DURATION' : 'DATED',
  scheduleStartDate: typeof data.startDate === 'string' ? data.startDate : typeof data.scheduleStartDate === 'string' ? data.scheduleStartDate : null,
  scheduleEndDate: typeof data.endDate === 'string' ? data.endDate : typeof data.scheduleEndDate === 'string' ? data.scheduleEndDate : null,
  hasTimeWindow: data.hasTimeWindow === true,
  scheduleTimeFrom: typeof data.scheduleTimeFrom === 'string' ? data.scheduleTimeFrom : null,
  scheduleTimeTo: typeof data.scheduleTimeTo === 'string' ? data.scheduleTimeTo : null,
  blockedByNodeIds: Array.isArray(data.blockedByNodeIds)
    ? data.blockedByNodeIds.filter((link) => link?.targetType === 'CHECKLIST' && typeof link.targetId === 'string')
    : [],
  isBlockerForNodeIds: Array.isArray(data.isBlockerForNodeIds)
    ? data.isBlockerForNodeIds.filter((link) => link?.targetType === 'CHECKLIST' && typeof link.targetId === 'string')
    : [],
  workflowStatus: data.workflowStatus,
  nodeKind: data.nodeKind === 'GATE_AND' || data.nodeKind === 'GATE_OR' ? data.nodeKind : 'CHECKLIST',
  gateInputIds: Array.isArray(data.gateInputIds)
    ? data.gateInputIds.filter((id): id is string => typeof id === 'string')
    : [],
  // Recurring fields must survive the normalize step. Without preserving
  // `checklistType` here, a reload path (Firestore → normalize) drops the
  // field and the recurring display filter (checklistType === lens.mode)
  // silently discards the item — reads as "the item vanished after switching
  // tabs and coming back". `occurrences` and `isArchived` are their siblings.
  checklistType: data.checklistType === 'DAILY' || data.checklistType === 'WEEKLY' || data.checklistType === 'INDIVIDUAL'
    ? data.checklistType
    : undefined,
  occurrences: data.occurrences && typeof data.occurrences === 'object'
    ? data.occurrences
    : undefined,
  isArchived: data.isArchived === true,
})
